import React, { useEffect, useMemo, useState } from 'react'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { Area, CopyShiftAllocationsModalMetaProps, Event, Shift, ShiftWithDuty } from '../data/types'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'
import { Button } from './ui/Button'
import { LoadingSpinner } from './LoadingSpinner'
import { Modal } from './ui/Modal'

type Props = {
    meta: CopyShiftAllocationsModalMetaProps
    isOpen: boolean
    sourceShift: ShiftWithDuty | null
    onClose: () => void
    onSubmit: (targetShiftId: number) => Promise<void>
}

const normalizeEvents = (result: any): Event[] => {
    let events = result.value || result

    if (Array.isArray(events) && events.length > 0 && Array.isArray(events[0])) {
        events = events[0]
    }

    return Array.isArray(events) ? events : []
}

export const CopyShiftAllocationsModal = (props: Props) => {
    const { meta, isOpen, sourceShift, onClose, onSubmit } = props

    const [events, setEvents] = useState<Event[]>([])
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchEvents = async () => {
            if (!isOpen || events.length > 0) return

            setLoadingEvents(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) throw new Error(meta.loadEventsError)

                const result = await response.json()
                setEvents(normalizeEvents(result))
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.loadEventsError)
            } finally {
                setLoadingEvents(false)
            }
        }

        fetchEvents()
    }, [events.length, isOpen, meta.loadEventsError])

    useEffect(() => {
        if (!isOpen) {
            setSelectedEventId(null)
            setSelectedAreaId(null)
            setSelectedShiftId(null)
            setError(null)
            setSubmitting(false)
        }
    }, [isOpen])

    useEffect(() => {
        setSelectedAreaId(null)
        setSelectedShiftId(null)
    }, [selectedEventId])

    useEffect(() => {
        setSelectedShiftId(null)
    }, [selectedAreaId])

    const selectedEvent = useMemo(() => events.find(event => event.id === selectedEventId) || null, [events, selectedEventId])
    const selectedArea = useMemo(() => selectedEvent?.areas?.find(area => area.id === selectedAreaId) || null, [selectedAreaId, selectedEvent])
    const selectableShifts = useMemo(() => {
        return sortShifts((selectedArea?.shifts || []) as Shift[]).filter(shift => shift.id !== sourceShift?.id)
    }, [selectedArea?.shifts, sourceShift?.id])

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        if (!selectedShiftId) {
            setError(meta.selectShiftError)
            return
        }

        setSubmitting(true)
        setError(null)

        try {
            await onSubmit(selectedShiftId)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="md"
            footer={
                <div className="flex justify-end gap-3">
                    <Button type="button" onClick={onClose} variant="outline" disabled={submitting}>
                        {meta.cancelButton}
                    </Button>
                    <Button type="submit" form="copy-shift-allocations-form" disabled={submitting || !selectedShiftId} color="primary">
                        {submitting ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="copy-shift-allocations-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {meta.description}
                    </p>

                    <div className="mb-4 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                            {meta.sourceShiftLabel}
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {sourceShift ? getShiftDisplayName(sourceShift) : ''}
                        </p>
                    </div>

                    {loadingEvents ? (
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.eventLabel}
                                </label>
                                <select
                                    value={selectedEventId ?? ''}
                                    onChange={(event) => setSelectedEventId(Number(event.target.value) || null)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    <option value="">{meta.selectEventPlaceholder}</option>
                                    {sortByName(events).map(event => (
                                        <option key={event.id} value={event.id}>{event.name}</option>
                                    ))}
                                </select>
                            </div>

                            {selectedEvent && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.areaLabel}
                                    </label>
                                    <select
                                        value={selectedAreaId ?? ''}
                                        onChange={(event) => setSelectedAreaId(Number(event.target.value) || null)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="">{meta.selectAreaPlaceholder}</option>
                                        {sortByName((selectedEvent.areas || []) as Area[]).map(area => (
                                            <option key={area.id} value={area.id}>{area.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {selectedArea && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.shiftLabel}
                                    </label>
                                    <select
                                        value={selectedShiftId ?? ''}
                                        onChange={(event) => setSelectedShiftId(Number(event.target.value) || null)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        disabled={selectableShifts.length === 0}
                                    >
                                        <option value="">{selectableShifts.length === 0 ? meta.noTargetShiftsMessage : meta.selectShiftPlaceholder}</option>
                                        {selectableShifts.map(shift => (
                                            <option key={shift.id} value={shift.id}>{getShiftDisplayName(shift)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    )
}
